import type {
	ClientAddressEntity,
	PortalAccountDeletionResultDto,
	PortalContactChangeConfirmDto,
	PortalContactChangeRequestDto,
	PortalContactChangeRequestResultDto,
	PortalContactRemoveDto,
	PortalProfileDto,
	PortalProfileUpdateDto,
	PortalSalesRepInfoDto,
} from '@supply-mover-2-0/shared';
import { portalFetch } from '../lib/apiClient';

/**
 * Fetch the portal user's profile.
 */
export async function getProfile(token: string): Promise<PortalProfileDto> {
	return portalFetch<PortalProfileDto>('/account/profile', token);
}

/**
 * Update the portal user's name. The email and phone change through the
 * contact-change calls below (PD-16726).
 */
export async function updateProfile(
	token: string,
	payload: PortalProfileUpdateDto,
): Promise<PortalProfileDto> {
	return portalFetch<PortalProfileDto>('/account/profile', token, {
		method: 'PUT',
		body: JSON.stringify(payload),
	});
}

/**
 * PD-16726 — send a 6-digit code to the new email address or phone number.
 * The answer carries the value as the server stored it (a phone comes back in
 * E.164); send exactly that value to confirm.
 */
export async function requestContactChange(
	token: string,
	payload: PortalContactChangeRequestDto,
): Promise<PortalContactChangeRequestResultDto> {
	return portalFetch<PortalContactChangeRequestResultDto>(
		'/account/contact-change/request',
		token,
		{ method: 'POST', body: JSON.stringify(payload) },
	);
}

/** PD-16726 — save the new value with the code sent to it. */
export async function confirmContactChange(
	token: string,
	payload: PortalContactChangeConfirmDto,
): Promise<PortalProfileDto> {
	return portalFetch<PortalProfileDto>(
		'/account/contact-change/confirm',
		token,
		{ method: 'POST', body: JSON.stringify(payload) },
	);
}

/** PD-16726 — remove the email address or the phone number. */
export async function removeContact(
	token: string,
	payload: PortalContactRemoveDto,
): Promise<PortalProfileDto> {
	return portalFetch<PortalProfileDto>(
		'/account/contact-change/remove',
		token,
		{ method: 'POST', body: JSON.stringify(payload) },
	);
}

/**
 * Delete the signed-in user's own account (PD-16721): their login and personal
 * details. The customer account and its orders, invoices and payments are kept.
 */
export async function deleteAccount(
	token: string,
): Promise<PortalAccountDeletionResultDto> {
	return portalFetch<PortalAccountDeletionResultDto>('/account', token, {
		method: 'DELETE',
	});
}

/**
 * Fetch addresses for the active client.
 */
export async function getAddresses(
	token: string,
): Promise<Array<ClientAddressEntity>> {
	return portalFetch<Array<ClientAddressEntity>>('/account/addresses', token);
}

/**
 * Fetch the assigned salesperson contact info for the active client
 * (PD-15235). Null when the portal site opts out or no rep is assigned —
 * callers hide the block.
 */
export async function getSalesRepInfo(
	token: string,
): Promise<PortalSalesRepInfoDto | null> {
	return portalFetch<PortalSalesRepInfoDto | null>('/account/sales-rep', token);
}
